import React, { useMemo } from "react";
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
  forceRadial,
  SimulationNodeDatum,
  SimulationLinkDatum,
} from "d3-force";
import chroma from "chroma-js";

const CUE_BG = ["#fff355", "#608ad6", "#e75151", "#92dc67", "#e17d47"];
const CUE_FG = ["#333333", "#ffffff", "#ffffff", "#ffffff", "#ffffff"];

interface GraphNode {
  id: string;
  name: string;
}

interface GraphEdge {
  from: string;
  to: string;
  accuracy: number;
}

interface SocialGraphCardProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
  size?: number;
}

interface SimNode extends SimulationNodeDatum {
  id: string;
  name: string;
  radius: number;
}

interface SimLink extends SimulationLinkDatum<SimNode> {
  source: string;
  target: string;
  mutual: number;
}

/** Edge pair for force layout (one per node pair) */
interface EdgePair {
  idA: string;
  idB: string;
  mutual: number;
}

// secondary.main (#e2e2e2) for weak → primary.main (#ec7777) for strong
// Cool blue (weak) → warm amber (mid) → primary pink-red (strong)
const edgeColorScale = chroma.scale(["#94b8d4", "#d4a76a", "#ec7777"]).domain([0, 1000, 2000]).mode("lab");

function accuracyToColor(accuracy: number): string {
  return edgeColorScale(accuracy).hex();
}

function accuracyToOpacity(accuracy: number): number {
  return 0.3 + Math.max(0, Math.min(1, accuracy / 2000)) * 0.7;
}

/** Build deduplicated pairs with mutual strength for force layout */
function buildPairsForLayout(edges: GraphEdge[]): EdgePair[] {
  const directed = new Map<string, number>();
  for (const e of edges) {
    directed.set(`${e.from}->${e.to}`, e.accuracy);
  }

  const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const seen = new Set<string>();
  const pairs: EdgePair[] = [];

  for (const e of edges) {
    const key = pairKey(e.from, e.to);
    if (seen.has(key)) continue;
    seen.add(key);

    const idA = e.from < e.to ? e.from : e.to;
    const idB = e.from < e.to ? e.to : e.from;
    const accAB = directed.get(`${idA}->${idB}`) ?? 0;
    const accBA = directed.get(`${idB}->${idA}`) ?? 0;

    pairs.push({ idA, idB, mutual: Math.sqrt(accAB * accBA) });
  }

  return pairs;
}

/** Run d3-force simulation and return positioned nodes */
function computeLayout(
  nodes: GraphNode[],
  pairs: EdgePair[],
  size: number
): { positions: Record<string, { x: number; y: number }>; nodeRadii: Record<string, number> } {
  const n = nodes.length;
  const nodeR = Math.round(16 * (size / 220));

  // Compute average incoming accuracy for node sizing
  const incomingSum: Record<string, number> = {};
  const incomingCount: Record<string, number> = {};
  // We need the original directed edges for this, but pairs don't have them.
  // Node sizing is less critical — use uniform radius for simplicity.
  const nodeRadii: Record<string, number> = {};
  for (const node of nodes) {
    nodeRadii[node.id] = nodeR;
  }

  // Build d3-force links (one per pair)
  const simLinks: SimLink[] = pairs.map((p) => ({
    source: p.idA,
    target: p.idB,
    mutual: p.mutual,
  }));

  // Initialize nodes on a circle
  const simNodes: SimNode[] = nodes.map((node, i) => {
    const angle = (2 * Math.PI * i) / n - Math.PI / 2;
    const initR = size * 0.35;
    return {
      id: node.id,
      name: node.name,
      radius: nodeRadii[node.id],
      x: size / 2 + initR * Math.cos(angle),
      y: size / 2 + initR * Math.sin(angle),
    };
  });

  // Target radius for the circular layout
  const orbitalR = size * 0.35;
  const maxDist = size * 0.5;
  const minDist = size * 0.15;

  const sim = forceSimulation<SimNode>(simNodes)
    .force(
      "link",
      forceLink<SimNode, SimLink>(simLinks)
        .id((d) => d.id)
        .distance((d) => {
          const t = Math.max(0, Math.min(1, d.mutual / 2000));
          return maxDist - t * (maxDist - minDist);
        })
        .strength((d) => {
          // Gentle link pull — radial force dominates the shape
          const t = Math.max(0, Math.min(1, d.mutual / 2000));
          return 0.05 + t * 0.15;
        })
    )
    .force("charge", forceManyBody().strength(-size * 2))
    .force("center", forceCenter(size / 2, size / 2).strength(0.1))
    .force("radial", forceRadial(orbitalR, size / 2, size / 2).strength(0.8))
    .force(
      "collide",
      forceCollide<SimNode>().radius((d) => d.radius * 2.5)
    )
    .stop();

  const ticks = Math.max(200, n * 40);
  for (let i = 0; i < ticks; i++) {
    sim.tick();
  }

  const positions: Record<string, { x: number; y: number }> = {};
  for (const node of simNodes) {
    const r = nodeRadii[node.id];
    const pad = r * 1.8;
    positions[node.id] = {
      x: Math.max(pad, Math.min(size - pad, node.x!)),
      y: Math.max(pad, Math.min(size - pad, node.y!)),
    };
  }

  return { positions, nodeRadii };
}


export default function SocialGraphCard({ nodes, edges, size = 220 }: SocialGraphCardProps) {
  const isPng = size !== 220;
  const scale = size / 220;
  const n = nodes.length;

  const pairs = useMemo(() => buildPairsForLayout(edges), [edges]);

  const { positions, nodeRadii } = useMemo(
    () => computeLayout(nodes, pairs, size),
    [nodes, pairs, size]
  );

  if (n === 0) return null;

  // Scale stroke width down as player count increases
  const maxOptimal = isPng ? 7 : 5;
  const strokeScale = Math.min(1, maxOptimal / n);

  // Arrowhead sizing
  const baseArrowSize = 6;
  const pageArrowScale = 0.6 + 0.4 * strokeScale;
  const arrowSize = isPng
    ? Math.round(baseArrowSize * scale)
    : Math.max(3, Math.round(baseArrowSize * pageArrowScale * 2));

  // Curvature: perpendicular offset for the bezier control point
  const curvature = size * 0.07;

  const markerSuffix = `${isPng ? "png" : "page"}-${arrowSize}`;

  // For 8+ players, hide weak edges to reduce clutter
  const visibilityThreshold = n >= 8 ? 400 : 0;

  // Filter visible edges
  const visibleEdges = edges.filter((e) => e.accuracy >= visibilityThreshold);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <defs>
        {visibleEdges.map((edge, i) => {
          const color = accuracyToColor(edge.accuracy);
          const opacity = accuracyToOpacity(edge.accuracy);
          const id = `arrow-${i}-${markerSuffix}`;
          return (
            <marker
              key={id}
              id={id}
              markerWidth={arrowSize}
              markerHeight={arrowSize}
              refX={arrowSize * 0.9}
              refY={arrowSize / 2}
              orient="auto"
              markerUnits="userSpaceOnUse"
            >
              <path
                d={`M0,0 L0,${arrowSize} L${arrowSize},${arrowSize / 2} z`}
                fill={color}
                opacity={opacity}
              />
            </marker>
          );
        })}
      </defs>

      {/* Curved arcs — two per pair, curving in opposite directions */}
      {visibleEdges.map((edge, i) => {
        const from = positions[edge.from];
        const to = positions[edge.to];
        if (!from || !to) return null;

        const fromR = nodeRadii[edge.from] ?? 16 * scale;
        const toR = nodeRadii[edge.to] ?? 16 * scale;

        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 1) return null;

        // Unit vectors along the edge
        const ux = dx / len;
        const uy = dy / len;

        // Perpendicular from CANONICAL direction (smaller ID → larger ID)
        // so both A→B and B→A use the same perpendicular base, just opposite signs
        const canonicalFrom = edge.from < edge.to ? from : to;
        const canonicalTo = edge.from < edge.to ? to : from;
        const cdx = canonicalTo.x - canonicalFrom.x;
        const cdy = canonicalTo.y - canonicalFrom.y;
        const clen = Math.sqrt(cdx * cdx + cdy * cdy);
        const cpx = -cdy / clen; // canonical perpendicular
        const cpy = cdx / clen;
        const sign = edge.from < edge.to ? 1 : -1;

        // Control point: midpoint offset perpendicular
        const mx = (from.x + to.x) / 2 + cpx * curvature * sign;
        const my = (from.y + to.y) / 2 + cpy * curvature * sign;

        // Start/end on node borders (along straight line — close enough for gentle curves)
        const sx = from.x + ux * fromR;
        const sy = from.y + uy * fromR;
        const ex = to.x - ux * toR;
        const ey = to.y - uy * toR;

        const color = accuracyToColor(edge.accuracy);
        const baseStroke = isPng
          ? 1 + (edge.accuracy / 2000) * 2.5
          : (1 + (edge.accuracy / 2000) * 2.5) / 2;
        const strokeWidth = baseStroke * strokeScale;
        const opacity = accuracyToOpacity(edge.accuracy);

        return (
          <path
            key={`arc-${i}`}
            d={`M ${sx},${sy} Q ${mx},${my} ${ex},${ey}`}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            opacity={opacity}
            markerEnd={`url(#arrow-${i}-${markerSuffix})`}
          />
        );
      })}

      {/* Draw nodes on top */}
      {nodes.map((node, idx) => {
        const pos = positions[node.id];
        if (!pos) return null;
        const r = nodeRadii[node.id] ?? 16 * scale;
        const shortName = node.name.length > 7 ? node.name.slice(0, 6) + "\u2026" : node.name;
        const bg = CUE_BG[idx % CUE_BG.length];
        const fg = CUE_FG[idx % CUE_FG.length];
        return (
          <g key={node.id}>
            <circle cx={pos.x} cy={pos.y} r={r} fill={bg} opacity={0.95} />
            <text
              x={pos.x}
              y={pos.y + r * 0.25}
              textAnchor="middle"
              fontSize={r * 0.5}
              fill={fg}
              fontWeight="bold"
            >
              {shortName}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
